export type SelectionAnchor = {
  offset: number;
  id: number;
  text: string;
};

export interface State {
  start: SelectionAnchor | null;
  end: SelectionAnchor | null;
  isCollapsed: boolean;
  isTouch: boolean;
}

const parentsUntilData = (key: string, el?: HTMLElement | null) => {
  let node = el;
  while (node) {
    const value = node.dataset[key];
    if (value !== undefined) {
      return {
        node,
        [key]: value,
      };
    }
    node = node.parentElement;
  }
};

const constructSelectionAnchor = (
  container: Node,
  internalOffset: number
): SelectionAnchor | undefined => {
  const directParent = container.parentElement;

  // the parent node that is not constructed due to user entries
  const paragraphPart = parentsUntilData("id", directParent);

  if (!paragraphPart) {
    return undefined;
  }

  const spanNode = parentsUntilData("offset", directParent);

  const adjustedOffset =
    internalOffset + (spanNode ? parseInt(spanNode.offset as string) : 0);

  return {
    id: parseInt(paragraphPart.id as string),
    offset: adjustedOffset,
    text: paragraphPart.node.textContent ?? "",
  };
};

export const useSelectionStore = defineStore("selection", {
  state: (): State => ({
    start: null,
    end: null,
    isCollapsed: true,
    isTouch: false,
  }),
  actions: {
    setTouch(isTouch: boolean) {
      this.isTouch = isTouch;
    },
    setSelection() {
      const sel = window.getSelection();

      if (sel === null || sel.anchorNode === null) return;

      const { startContainer, startOffset } = sel.getRangeAt(0);

      const { endContainer, endOffset } = sel.getRangeAt(sel.rangeCount - 1);

      let start = constructSelectionAnchor(startContainer, startOffset);
      let end = constructSelectionAnchor(endContainer, endOffset);

      if (start === undefined || end === undefined) {
        this.resetSelection();
        return;
      }

      // didn't know about this API until today
      // it uses bitmasks to tell about the position of Nodes
      // IMPORTANT: the single & is a bitwise AND
      // https://developer.mozilla.org/en-US/docs/Web/API/Node/compareDocumentPosition
      // if end comes before start, swap them
      if (
        startContainer.compareDocumentPosition(endContainer) &
        Node.DOCUMENT_POSITION_PRECEDING
      ) {
        [start, end] = [end, start];
      }

      this.isCollapsed = sel.isCollapsed;

      this.start = start;

      this.end = end;
    },

    resetSelection(affectDocument = true) {
      this.start = null;
      this.end = null;
      this.isCollapsed = true;
      this.isTouch = false;
      if (affectDocument) {
        const sel = document.getSelection();
        if (sel && sel.rangeCount > 0) {
          sel.collapseToStart();
        }
      }
    },
  },
});
